import { useState } from 'react';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import Calendar from '@Components/MuiWrappers/Calendar';
import TextField from '@mui/material/TextField';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';

const FacetedSearchDateRangeField = (props) => {

    const today = new Date();
    const calendarMin = new Date(today.getFullYear() - 20, today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const calendarMax = new Date(today.getFullYear() + (props.EnableFutureDates ? 2 : 0), today.getMonth(), today.getDate(), 23, 59, 59, 0);
    const minCalendarId = 'min-date-calendar-' + props.FieldName;
    const maxCalendarId = 'max-date-calendar-' + props.FieldName;

    const windowSize = useWindowSize();
    const [gte, setGte] = useState(props.GTEDays || '');
    const [lte, setLte] = useState(props.LTEDays || '');
    const [minCalendarDisabled, setMinCalendarDisabled] = useState(props.DisableMinCalendar || (props.MaximumDate && !props.MinimumDate));
    const [maxCalendarDisabled, setMaxCalendarDisabled] = useState(props.DisableMaxCalendar || (!props.MaximumDate && props.MinimumDate));
    const [minDate, setMinDate] = useState(props.MinimumDate ? new Date(props.MinimumDate) : (minCalendarDisabled ? calendarMin : new Date(today.getFullYear(), today.getMonth(), today.getDate() - 180, 0, 0, 0, 0)));
    const [maxDate, setMaxDate] = useState(props.MaximumDate ? new Date(props.MaximumDate) : calendarMax);

    const applySearch = () => {
        if (gte) {
            runSearch(gte + '*');
        } else if (lte) {
            runSearch('*' + lte);
        } else {
            let minimumDate = new Date(minDate);
            let maximumDate = new Date(maxDate);
            minimumDate.setHours(0, 0, 0, 0);
            maximumDate.setHours(23, 59, 59, 0);
            if (isValidDateRange(minimumDate, maximumDate)) {
                runSearch(minimumDate.toISOString() + '*' + maximumDate.toISOString());
            }
        }
    };

    const runSearch = (value) => {
        props.RunSearch({
            fieldName: props.FieldName,
            value: value
        });
    };

    const isValidDateRange = (minimumDate, maximumDate) => {
        const isValidMin = minimumDate.getTime() >= calendarMin.getTime() && minimumDate.getTime() <= maximumDate.getTime();
        const isValidMax = maximumDate.getTime() <= calendarMax.getTime();
        return isValidMin && isValidMax;
    }

    const validateDays = (e, current) => {
        let days = 0;
        if (e.target.value) {
            if (!Number.isInteger(Number(e.target.value))) {
                days = current;
            } else {
                let input = Number(e.target.value);
                if (input <= 365 && input >= 1) {
                    days = input;
                } else {
                    days = current;
                }
            }
        }
        return days;
    };

    const onKeyPress = (e) => {
        if (e.key == 'Enter') {
            applySearch();
        }
    };

    const onMinChange = (date) => {
        setMinDate(date);
        setGte('');
        setLte('');
        enableMaxCalendar();
    };

    const onMaxChange = (date) => {
        setMaxDate(date);
        setGte('');
        setLte('');
        enableMinCalendar();
    };

    const onGteChange = (e) => {
        setLte('');
        let days = validateDays(e, gte);
        if (days) {
            setGte(days);
            setMinDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - days, 0, 0, 0, 0));
            enableMinCalendar();
            setMaxDate(calendarMax);
            setMaxCalendarDisabled(true);
        } else {
            setGte('');
            enableMaxCalendar();
        }
    };

    const onLteChange = (e) => {
        setGte('');
        let days = validateDays(e, lte);
        if (days) {
            setLte(days);
            setMaxDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - days, 23, 59, 59, 0));
            enableMaxCalendar();
            setMinDate(calendarMin);
            setMinCalendarDisabled(true);
        } else {
            setLte('');
            enableMinCalendar();
        }
    };

    const enableMinCalendar = () => {
        if (!props.DisableMinCalendar) {
            setMinCalendarDisabled(false);
        }
    };

    const enableMaxCalendar = () => {
        if (!props.DisableMaxCalendar) {
            setMaxCalendarDisabled(false);
        }
    };

    const sx = {
        '& .MuiOutlinedInput-root.Mui-focused': {
            '& > fieldset': {
                borderColor: 'black'
            }
        },
        backgroundColor: 'white',
        width: '100%',
        input: {
            padding: '5px',
            fontSize: '12px',
            color: 'black'
        },
        button: {
            margin: '0px'
        },
        svg: {
            width: '15px',
            height: '15px'
        }
    };

    return (
        <div className='faceted-date-range'>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}><TranslationText tKey='sandhills.tradeSites.mvc.selectadaterange' />:</div>
            <div className='faceted-range-container'>
                <div className='faceted-range'>
                    <div className='faceted-range-input-container'>
                        <label htmlFor={minCalendarId} className='visually-hidden'>Date From</label>
                        <Calendar
                            id={minCalendarId}
                            value={minDate}
                            onChange={onMinChange}
                            disabled={minCalendarDisabled}
                            minDate={calendarMin}
                            maxDate={maxDate}
                            disableFuture={!props.EnableFutureDates}
                            inputFormat={props.DateFormat}
                            DialogProps={{
                                sx: {
                                    zIndex: 1600
                                }
                            }}
                            InputAdornmentProps={{
                                sx: {
                                    margin: '0px'
                                }
                            }}
                            sx={sx}
                        />
                    </div>
                    <span className='faceted-range-divider'>-</span>
                    <div className='faceted-range-input-container'>
                        <label htmlFor={maxCalendarId} className='visually-hidden'>Date To</label>
                        <Calendar
                            id={maxCalendarId}
                            value={maxDate}
                            onChange={onMaxChange}
                            disabled={maxCalendarDisabled}
                            minDate={minDate}
                            maxDate={calendarMax}
                            disableFuture={!props.EnableFutureDates}
                            inputFormat={props.DateFormat}
                            DialogProps={{
                                sx: {
                                    zIndex: 1600
                                }
                            }}
                            InputAdornmentProps={{
                                sx: {
                                    margin: '0px'
                                }
                            }}
                            sx={sx}
                        />
                    </div>
                </div>
            </div>
            {(props.ShowGTEDaysSearch || props.ShowLTEDaysSearch) &&
                <div style={{ fontWeight: 'bold', marginTop: '10px', display: 'inline-block' }}><TranslationText tKey='sandhills.tradeSites.mvc.orenternumberofdays' />:</div>}
            {props.ShowGTEDaysSearch &&
                <div className='days-ago-container'>
                    {props.GTEDaysDisplayText}
                    <label htmlFor='greater-than-days-ago-input' className='visually-hidden'>Number of Days</label>
                    <input id='greater-than-days-ago-input' type='search' className='days-ago-input' value={gte} onChange={onGteChange} onKeyDown={onKeyPress} />
                    {translator.translate('sandhills.days').toLowerCase()}
                </div>}
            {props.ShowLTEDaysSearch &&
                <div className='days-ago-container'>
                    {props.LTEDaysDisplayText}
                    <label htmlFor='less-than-days-ago-input' className='visually-hidden'>Number of Days</label>
                    <input id='less-than-days-ago-input' type='search' className='days-ago-input' value={lte} onChange={onLteChange} onKeyDown={onKeyPress} />
                    {translator.translate('sandhills.days').toLowerCase()}
                </div>}
            <button className='ts-button faceted-search' onClick={applySearch}>
                {windowSize.isMobile ? <TranslationText tKey='sandhills.apply' /> : <TranslationText tKey='sandhills.tradepubwebsite.mvc.search' />}
            </button>
        </div>
    );
};

export default FacetedSearchDateRangeField;